import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { formatStandard } from '../utils/standardLabel';
import './Checkout.css';

const Checkout = () => {
  const [cart, setCart] = useState([]);
  const [deliveryType, setDeliveryType] = useState('school_delivery');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [updatingItems, setUpdatingItems] = useState(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const response = await api.get('/cart');
      setCart(response.data.cart);
      if (response.data.cart.length === 0) {
        navigate('/cart');
      }
      setError('');
    } catch (err) {
      const errorMessage = err.userFriendlyMessage || 'Failed to load cart. Please try again.';
      setError(errorMessage);
      console.error(err);
      if (showLoading) {
        setTimeout(() => {
          navigate('/schools');
        }, 3000);
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const calculateSubtotal = () => {
    return cart.reduce((total, item) => {
      return total + parseFloat(item.price) * item.quantity;
    }, 0);
  };

  const calculateDeliveryCharge = () => {
    if (deliveryType !== 'home_delivery') {
      return 0;
    }
    
    // Count unique kits in cart
    const uniqueKits = new Set(cart.map(item => item.kit_id));
    const kitCount = uniqueKits.size;
    
    if (kitCount === 1) {
      return 150;
    } else if (kitCount >= 2) {
      return 300;
    }
    
    return 0;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateDeliveryCharge();
  };

  const updateQuantity = async (cartId, newQuantity) => {
    if (newQuantity < 1) {
      removeItem(cartId);
      return;
    }
    setUpdatingItems(prev => new Set(prev).add(cartId));
    try {
      await api.put(`/cart/${cartId}`, { quantity: newQuantity });
      await fetchCart(false);
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update quantity');
    } finally {
      setUpdatingItems(prev => {
        const next = new Set(prev);
        next.delete(cartId);
        return next;
      });
    }
  };

  const removeItem = async (cartId) => {
    if (!window.confirm('Remove this item from your order?')) return;
    setUpdatingItems(prev => new Set(prev).add(cartId));
    try {
      await api.delete(`/cart/${cartId}`);
      await fetchCart(false);
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove item');
    } finally {
      setUpdatingItems(prev => {
        const next = new Set(prev);
        next.delete(cartId);
        return next;
      });
    }
  };

  const handlePayment = async () => {
    if (deliveryType === 'home_delivery' && !deliveryAddress.trim()) {
      setError('Please provide delivery address for home delivery');
      return;
    }

    setProcessing(true);
    setError('');

    try {
      // Create order
      const orderResponse = await api.post('/orders', {
        delivery_type: deliveryType,
        delivery_address: deliveryAddress
      });

      const { order, hdfc_order_id, payment_url, amount } = orderResponse.data;

      // Check if payment URL is provided
      if (!payment_url) {
        setError('Payment gateway not configured properly. Please contact support.');
        setProcessing(false);
        return;
      }

      // Redirect to HDFC payment page
      // The payment page will redirect back to order-confirmation page after payment
      window.location.href = payment_url;
      
      // Note: Processing state will remain true as user is redirected
      // The order confirmation page will handle verification
    } catch (err) {
      const errorMessage = err.userFriendlyMessage || err.response?.data?.error || 'Failed to process order. Please try again.';
      setError(errorMessage);
      setProcessing(false);
      console.error('Order creation error:', err);
      
      // Redirect to schools page after showing error
      setTimeout(() => {
        navigate('/schools');
      }, 3000);
    }
  };

  if (loading) {
    return (
      <div className="App">
        <Header />
        <div className="page">
          <div className="container">
            <div className="loading">Loading checkout...</div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Header />
      <div className="checkout-page">
        <div className="container">
          <div className="checkout-header">
            <div className="checkout-header-content">
              <h1 className="checkout-page-title">
                <span className="checkout-icon">💳</span>
                Checkout
              </h1>
              <p className="checkout-subtitle">
                Review your order and complete payment
              </p>
            </div>
          </div>

          {error && (
            <div className="checkout-error">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <span>{error}</span>
            </div>
          )}

          <div className="checkout-content">
            <div className="checkout-form">
              <div className="checkout-section">
                <div className="section-header">
                  <div className="section-icon-wrapper">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                  </div>
                  <h2 className="section-title">Delivery Options</h2>
                </div>
                <div className="delivery-options">
                  <label className={`delivery-option ${deliveryType === 'school_delivery' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="delivery"
                      value="school_delivery"
                      checked={deliveryType === 'school_delivery'}
                      onChange={(e) => setDeliveryType(e.target.value)}
                    />
                    <div className="delivery-option-content">
                      <div className="delivery-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                        </svg>
                      </div>
                      <div className="delivery-details">
                        <strong>School Delivery (Pickup)</strong>
                        <p>Collect your order from the school - Free delivery</p>
                      </div>
                      <div className="delivery-price">Free</div>
                    </div>
                  </label>
                  <label className={`delivery-option ${deliveryType === 'home_delivery' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="delivery"
                      value="home_delivery"
                      checked={deliveryType === 'home_delivery'}
                      onChange={(e) => setDeliveryType(e.target.value)}
                    />
                    <div className="delivery-option-content">
                      <div className="delivery-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                          <circle cx="12" cy="10" r="3"></circle>
                        </svg>
                      </div>
                      <div className="delivery-details">
                        <strong>Home Delivery</strong>
                        <p>Delivered to your address - Convenient and secure</p>
                        {cart.length > 0 && (
                          <p style={{ fontSize: '12px', marginTop: '4px', color: '#666' }}>
                            {(() => {
                              const uniqueKits = new Set(cart.map(item => item.kit_id));
                              const kitCount = uniqueKits.size;
                              return kitCount === 1 ? '₹150 for 1 kit' : `₹300 for ${kitCount} kits`;
                            })()}
                          </p>
                        )}
                      </div>
                      <div className="delivery-price">
                        {cart.length > 0 ? `₹${calculateDeliveryCharge()}` : '₹150'}
                      </div>
                    </div>
                  </label>
                </div>

                {deliveryType === 'home_delivery' && (
                  <div className="address-input-group">
                    <label className="address-label">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                        <circle cx="12" cy="10" r="3"></circle>
                      </svg>
                      Delivery Address *
                    </label>
                    <textarea
                      value={deliveryAddress}
                      onChange={(e) => setDeliveryAddress(e.target.value)}
                      rows="4"
                      placeholder="Enter your complete delivery address including street, city, state, and pincode"
                      className="address-textarea"
                      required
                    />
                  </div>
                )}
              </div>

              <div className="checkout-section">
                <div className="section-header section-header-with-edit">
                  <div className="section-icon-wrapper">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                      <line x1="3" y1="6" x2="21" y2="6"></line>
                      <path d="M16 10a4 4 0 0 1-8 0"></path>
                    </svg>
                  </div>
                  <h2 className="section-title">Order Items</h2>
                  <Link to="/cart" className="edit-cart-link" aria-label="Edit cart and change quantity">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                    <span>Edit cart</span>
                  </Link>
                </div>
                <div className="order-items">
                  {cart.map((item, index) => (
                    <div
                      key={item.id}
                      className={`order-item-card ${updatingItems.has(item.id) ? 'updating' : ''}`}
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <div className="order-item-main">
                        <div className="order-item-badge">
                          <span>{formatStandard(item.standard)}</span>
                        </div>
                        <div className="order-item-info">
                          <h3 className="order-item-title">{item.kit_name}</h3>
                          <div className="order-item-meta">
                            <div className="meta-item">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                              </svg>
                              <span>{item.school_name}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="order-item-controls">
                        <div className="order-item-quantity-wrap">
                          <span className="order-item-quantity-label">Quantity</span>
                          <div className="order-item-quantity-controls">
                            <button
                              type="button"
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              className="order-qty-btn order-qty-minus"
                              disabled={updatingItems.has(item.id)}
                              aria-label="Decrease quantity"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                              </svg>
                            </button>
                            <span className="order-qty-value">{item.quantity}</span>
                            <button
                              type="button"
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              className="order-qty-btn order-qty-plus"
                              disabled={updatingItems.has(item.id)}
                              aria-label="Increase quantity"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                              </svg>
                            </button>
                          </div>
                        </div>
                        <div className="order-item-price">
                          ₹{(parseFloat(item.price) * item.quantity).toFixed(2)}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeItem(item.id)}
                          className="order-item-remove-btn"
                          disabled={updatingItems.has(item.id)}
                          aria-label="Remove item"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          </svg>
                          <span>Remove</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="checkout-summary-card">
              <div className="summary-header">
                <div className="summary-icon-wrapper">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="1" x2="12" y2="23"></line>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                  </svg>
                </div>
                <h2 className="summary-title">Payment Summary</h2>
              </div>
              
              <div className="summary-content">
                <div className="summary-row">
                  <span className="summary-label">Subtotal</span>
                  <span className="summary-value">₹{calculateSubtotal().toFixed(2)}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Delivery Charge</span>
                  <span className="summary-value">
                    {calculateDeliveryCharge() === 0 ? (
                      <span className="free-badge">Free</span>
                    ) : (
                      `₹${calculateDeliveryCharge().toFixed(2)}`
                    )}
                  </span>
                </div>
                
                <div className="summary-divider"></div>
                
                <div className="summary-total-row">
                  <span className="total-label">Total Amount</span>
                  <span className="total-amount">₹{calculateTotal().toFixed(2)}</span>
                </div>
              </div>

              <div className="summary-footer">
                <button
                  onClick={handlePayment}
                  className="checkout-payment-btn"
                  disabled={processing}
                >
                  {processing ? (
                    <>
                      <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" strokeOpacity="0.25"></circle>
                        <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"></path>
                      </svg>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <span>Proceed to Payment</span>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14"></path>
                        <path d="M12 5l7 7-7 7"></path>
                      </svg>
                    </>
                  )}
                </button>
                <div className="payment-security">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                  <span>Secure payment powered by HDFC Bank</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Checkout;

